<template>
  <q-page padding class="employee-performance">
    <div class="row items-center q-mb-md">
      <div><h1 class="text-h5 q-my-none">{{ t('title') }}</h1><p class="text-grey-7 q-mb-none">{{ t('subtitle') }}</p></div>
      <q-space /><q-btn v-if="managerAccess" flat icon="tune" :label="t('targets')" :disable="!warehouse" @click="showTargets = true" />
    </div>
    <q-banner v-if="initError" class="bg-red-1 text-negative q-mb-md">{{ t('accessError') }} <q-btn flat :label="t('retry')" @click="initialize" /></q-banner>
    <q-spinner v-if="!managerAccess && !initError" color="primary" />
    <template v-if="managerAccess">
    <q-card flat bordered class="q-mb-md">
      <q-card-section class="ep-filters">
        <q-select v-model="facility" use-input hide-selected fill-input :input-debounce="300" :loading="facilityLoading" @filter="searchFacilities" class="col-12 col-md-3" :options="facilities" option-value="id" option-label="name" emit-value map-options clearable :label="t('facility')" @update:model-value="selectFacility" />
        <q-select v-model="warehouse" use-input hide-selected fill-input :input-debounce="300" :disable="!facility || optionsLoading" :loading="optionsLoading" @filter="searchWarehouses" class="col-12 col-md-3" :options="selectableWarehouses" option-value="id" option-label="name" emit-value map-options :label="t('warehouse')" @update:model-value="selectWarehouse" />
        <q-select v-model="operation" class="col-12 col-md-3" :options="operations" emit-value map-options :label="t('operation')" @update:model-value="resetEmployee" />
        <q-input v-model="from" class="col-6 col-md-2" type="date" :label="t('from')" />
        <q-input v-model="to" class="col-6 col-md-2" type="date" :label="t('to')" />
        <q-btn class="col-12 col-md-2" color="primary" icon="refresh" :label="t('refresh')" :loading="loading" :disable="!warehouse" @click="refresh" />
      </q-card-section>
      <q-card-actions><q-btn flat :label="t('today')" @click="setPeriod(false)" /><q-btn flat :label="t('month')" @click="setPeriod(true)" /><q-space /><span class="text-caption text-grey-7">{{ warehouse ? (selectedWarehouse?.timezone || t('timezoneMissing')) : '' }}</span></q-card-actions>
    </q-card>
    <q-banner v-if="error" class="bg-red-1 text-negative q-mb-md">{{ t('refreshError') }}</q-banner>
    <q-banner v-if="!warehouse && !initError" class="bg-blue-1 q-mb-md">{{ t(facility ? 'selectWarehouse' : 'selectFacility') }}</q-banner>
    <template v-if="report">
      <div class="row q-col-gutter-md q-mb-md">
        <div v-for="stage in stageNames" :key="stage" class="col-12 col-md-4">
          <q-card flat bordered class="metric-card"><q-card-section>
            <div class="text-subtitle2">{{ t(stage) }}</div>
            <div class="text-h4">{{ value(report.summary[stage].output) }}</div>
            <div class="text-caption">{{ t(stage === 'assembly' ? 'orders' : 'units') }}</div>
            <div class="q-mt-sm text-grey-7">{{ t('completed') }}: {{ value(report.summary[stage].completed) }}</div>
            <div v-if="!report.availability[stage].available" class="text-caption text-grey-7">{{ t(report.availability[stage].reason) }}</div>
          </q-card-section></q-card>
        </div>
      </div>
      <q-card flat bordered class="q-mb-md"><q-card-section class="row q-col-gutter-md">
        <div v-for="metric in ['speed', 'quality', 'attendance']" :key="metric" class="col-12 col-md-4"><div class="text-subtitle2">{{ t(metric) }}</div><div>{{ t('unavailable') }}</div><div class="text-caption text-grey-7">{{ t(report.availability[metric].reason) }}</div></div>
      </q-card-section></q-card>
      <div class="text-caption text-grey-7 q-mb-md">{{ t('coverage') }}: {{ report.coverage.attributedCompletions }} / {{ report.coverage.provenCompletions }}. {{ t('coverageDefinition') }} {{ t('updated') }}: {{ report.refreshedAt }}</div>
      <q-table flat bordered class="q-mb-md" :rows-per-page-label="$t('Rows per page')" :title="t('employees')" :rows="employeeRows" :columns="columns" :row-key="rowKey" :pagination="{ rowsPerPage: 25 }" @row-click="openEmployee">
        <template #body-cell-employeeName="props"><q-td :props="props"><q-btn flat dense no-caps color="primary" :label="props.row.employeeName || t('unattributed')" @click.stop="openEmployee(null, props.row)" /></q-td></template>
        <template #no-data><div class="q-pa-md">{{ t('empty') }}</div></template>
      </q-table>
      <q-card flat bordered class="q-mb-md"><q-card-section><div class="text-h6">{{ t('daily') }}</div><div v-if="!report.daily.length" class="text-grey-7">{{ t('empty') }}</div>
        <div v-for="day in dailyRows" :key="day.date + day.operation" class="trend-row"><span>{{ day.date }} · {{ t(day.operation) }}</span><div class="trend-track"><div :style="{ width: trendWidth(day) + '%' }" /></div><strong>{{ value(day.output) }} {{ t(day.operation === 'assembly' ? 'orders' : 'units') }}</strong></div>
      </q-card-section></q-card>
      <q-expansion-item icon="info" :label="t('definitions')" class="bordered"><q-card><q-card-section><p>{{ t('definitionText') }}</p><p>{{ t('achievement') }}: {{ t('achievementDefinition') }}</p><p class="q-mb-none">{{ t('timingDefinition') }}</p></q-card-section></q-card></q-expansion-item>
    </template>
    <q-dialog v-model="showEmployee" maximized transition-show="slide-up" transition-hide="slide-down"><q-card class="employee-profile"><q-card-section class="row items-center"><div class="text-h6">{{ employeeName || t('unattributed') }}<div class="text-caption text-grey-7">{{ t(employeeOperation) }} · {{ selectedWarehouse?.name }} · {{ from }} — {{ to }}</div></div><q-space /><q-btn flat icon="close" :aria-label="t('close')" v-close-popup /></q-card-section>
      <q-btn flat icon="link" :label="$t('reportParity.copyLink')" @click="copyEmployeeLink" /><q-banner v-if="linkStatus" class="bg-grey-2">{{ $t(`reportParity.${linkStatus}`) }}</q-banner><q-tabs v-model="profileTab" align="left" active-color="primary" indicator-color="primary" class="profile-tabs"><q-tab name="summary" :label="t('profileOverview')" /><q-tab name="hourly" :label="t('profileHourly')" /><q-tab name="daily" :label="t('profileDaily')" /><q-tab name="records" :label="t('profileRecords')" /></q-tabs>
      <q-banner v-if="detailError" class="bg-red-1 text-negative">{{ t('refreshError') }}</q-banner>
      <q-inner-loading :showing="detailLoading" />
      <q-card-section v-if="detail">
        <div v-if="profileTab === 'summary'" class="profile-summary q-mb-md">
          <div><small>{{ t('completed') }}</small><strong>{{ value(detail.employees[0]?.completed) }}</strong></div>
          <div><small>{{ t('output') }} · {{ t(employeeOperation === 'assembly' ? 'orders' : 'units') }}</small><strong>{{ value(detail.employees[0]?.output) }}</strong></div>
          <div><small>{{ t('activityDays') }}</small><strong>{{ value(detail.employees[0]?.recordedDays) }}</strong></div>
          <div><small>{{ t('achievement') }}</small><strong>{{ percent(detail.employees[0]?.achievement) }}</strong></div>
        </div>
        <template v-if="detail.employeeProfile">
          <section v-if="profileTab === 'hourly'">
          <h3 class="text-subtitle1">{{ t('hourly') }}</h3>
          <p class="text-caption">{{ t('hourlyDefinition') }} · {{ detail.employeeProfile.timezone }}</p>
          <q-select :model-value="detail.employeeProfile.date" :options="detail.employeeProfile.daily.map(day => day.date)" :label="t('date')" @update:model-value="selectDetailDay" />
          <p class="text-caption q-mt-sm">{{ t('output') }} ({{ t(employeeOperation === 'assembly' ? 'orders' : 'units') }}) · {{ t('completionsShort') }}</p>
          <div class="hour-chart q-my-md" role="list" :aria-label="t('hourly')">
            <div v-for="hour in detail.employeeProfile.hourly" :key="hour.start" class="hour-row" role="listitem">
              <span>{{ hour.label }}</span><div class="trend-track"><div :style="{ width: profileWidth(hour, detail.employeeProfile.hourly) + '%' }" /></div>
              <span>{{ value(hour.output) }} · {{ value(hour.completed) }} {{ t('completionsShort') }}</span>
            </div>
          </div>
          <p class="text-caption">{{ t('firstCompletion') }}: {{ detail.employeeProfile.firstCompletion || t('unavailable') }}<br>{{ t('lastCompletion') }}: {{ detail.employeeProfile.lastCompletion || t('unavailable') }}</p>
          </section><section v-if="profileTab === 'daily'">
          <h3 class="text-subtitle1">{{ t('employeeDaily') }}</h3>
          <div v-for="day in detail.employeeProfile.daily" :key="day.date" class="hour-row q-my-xs">
            <q-btn flat dense no-caps :label="day.date" @click="selectDetailDay(day.date)" />
            <div class="trend-track"><div :style="{ width: profileWidth(day, detail.employeeProfile.daily) + '%' }" /></div>
            <span>{{ value(day.output) }} · {{ day.completed }} {{ t('completionsShort') }}</span>
          </div>
          </section>
        </template>
        <section v-if="profileTab === 'records'"><h3 class="text-subtitle1">{{ t('periodRecords') }}</h3><q-markup-table flat><thead><tr><th>{{ t('date') }}</th><th>{{ t('record') }}</th><th>{{ t('output') }}</th></tr></thead><tbody><tr v-for="record in detail.records.items" :key="record.operation + record.workId"><td>{{ record.completedAt }}</td><td>#{{ record.sourceId }}</td><td>{{ value(record.output) }}</td></tr></tbody></q-markup-table><div class="row justify-center q-mt-md"><q-pagination :max-pages="7" boundary-numbers direction-links v-model="detailPage" :max="Math.max(1, Math.ceil(detail.records.total / detail.records.perPage))" @update:model-value="loadDetail" /></div></section><q-banner v-if="profileTab === 'summary'" class="bg-blue-1 q-mt-lg">{{ t('hourlyDefinition') }}<div class="q-mt-md"><q-btn unelevated color="primary" :label="t('hourly')" @click="profileTab = 'hourly'" /></div></q-banner></q-card-section>
    </q-card></q-dialog>
    <q-dialog v-model="showTargets"><q-card class="detail-card"><q-card-section class="row items-center"><div class="text-h6">{{ t('targets') }}</div><q-space /><q-btn flat icon="close" :aria-label="t('close')" v-close-popup /></q-card-section>
      <q-card-section><p>{{ t('targetDefinition') }}</p><q-banner v-if="targetError" class="bg-red-1 text-negative q-mb-md">{{ t('targetError') }}</q-banner>
        <q-form @submit="saveTarget"><q-select v-model="targetOperation" :options="operations" emit-value map-options :label="t('operation')" /><q-input v-model="targetValue" type="number" min="1" max="100000000" step="1" :label="t('dailyTarget')" /><q-input v-model="targetDate" type="date" :min="tomorrow" :label="t('effectiveFrom')" /><q-btn type="submit" color="primary" class="q-mt-md" :label="t('save')" :loading="saving" :disable="!report?.targets.available || !selectedWarehouse?.timezone" /></q-form>
        <div v-if="!report?.targets.available" class="text-warning q-mt-md">{{ t('targetsUnavailable') }}</div>
        <q-markup-table v-if="report?.targets.items.length" flat class="q-mt-md"><thead><tr><th>{{ t('operation') }}</th><th>{{ t('dailyTarget') }}</th><th>{{ t('effectiveFrom') }}</th></tr></thead><tbody><tr v-for="target in report.targets.items" :key="target.id"><td>{{ t(target.operation) }}</td><td>{{ target.dailyTarget }}</td><td>{{ target.effectiveFrom }}</td></tr></tbody></q-markup-table>
      </q-card-section>
    </q-card></q-dialog>
    </template>
  </q-page>
</template>

<script>
// Lazy report routes use the initialized HTTP client at mount time.
import { employeePerformanceService } from '../../services/requests/employee-performance.service.js';
import { loadLatestPerformance } from '../../helpers/employee-performance.js';

export default {
  name: 'EmployeePerformance',
  data() {
    return {
      warehouses: [],
      optionsVersion: 0,
      facilitySearchVersion: 0,
      warehouseSearchVersion: 0,
      facilityLoading: false,
      optionsLoading: false,
      facilities: [],
      facility: null,
      warehouse: null,
      operation: 'picking',
      from: '',
      to: '',
      report: null,
      loading: false,
      error: false,
      initError: false,
      managerAccess: false,
      requestVersion: 0,
      showEmployee: false,
      profileTab: 'summary', linkStatus: '',
      employee: null,
      employeeName: '',
      employeeOperation: 'picking',
      detail: null,
      detailPage: 1,
      detailLoading: false,
      detailError: false,
      detailVersion: 0,
      detailDate: '',
      showTargets: false,
      targetOperation: 'picking',
      targetValue: '',
      targetDate: '',
      saving: false,
      targetError: false,
      stageNames: ['receiving', 'picking', 'assembly'],
    };
  },
  computed: {
    selectableWarehouses() { return this.warehouses; },
    employeeRows() { return (this.report?.employees || []).filter((row) => row.operation === this.operation); },
    dailyRows() { return (this.report?.daily || []).filter((row) => row.operation === this.operation); },
    selectedWarehouse() { return this.warehouses.find((w) => w.id === this.warehouse); },
    operations() { return this.stageNames.map((value) => ({ value, label: this.t(value) })); },
    tomorrow() {
      const today = this.selectedWarehouse?.today;
      if (!today) return '';
      const date = new Date(`${today}T00:00:00Z`); date.setUTCDate(date.getUTCDate() + 1);
      return date.toISOString().slice(0, 10);
    },
    columns() {
      return [
        {
          name: 'employeeName', label: this.t('employee'), field: (r) => r.employeeName || this.t('unattributed'), align: 'left', sortable: true,
        },
        {
          name: 'operation', label: this.t('operation'), field: (r) => this.t(r.operation), sortable: true,
        },
        {
          name: 'completed', label: this.t('completed'), field: 'completed', sortable: true,
        },
        {
          name: 'output', label: this.t('output'), field: 'output', format: this.value, sortable: true,
        },
        {
          name: 'recordedDays', label: this.t('activityDays'), field: 'recordedDays', sortable: true,
        },
        {
          name: 'achievement', label: this.t('achievement'), field: 'achievement', format: this.percent, sortable: true,
        },
      ];
    },
  },
  watch: {
    showEmployee(value) { if (!value) { this.detailVersion += 1; this.detail = null; } },
  },
  mounted() { this.initialize(); },
  beforeUnmount() { this.requestVersion += 1; this.detailVersion += 1; this.optionsVersion += 1; this.facilitySearchVersion += 1; this.warehouseSearchVersion += 1; },
  methods: {
    async restoreEmployeeLink() {
      const query = this.$route.query;
      if (!query.employee) return;
      const facility = Number(query.facility); const warehouse = Number(query.warehouse);
      if (!Number.isInteger(facility) || facility < 1 || !Number.isInteger(warehouse) || warehouse < 1
        || !['receiving', 'picking', 'assembly'].includes(query.operation)
        || !/^\d{4}-\d{2}-\d{2}$/.test(query.from || '') || !/^\d{4}-\d{2}-\d{2}$/.test(query.to || '')
        || !(query.employee === 'unattributed' || /^[1-9]\d*$/.test(query.employee))) { this.initError = true; return; }
      // The shared HTTP client cancels concurrent GETs to the same endpoint.
      const facilities = await employeePerformanceService.options({ search: String(facility) });
      const warehouses = await employeePerformanceService.options({ facility, search: String(warehouse) });
      this.facilities = facilities.facilities; this.warehouses = warehouses.warehouses;
      if (!this.facilities.some((item) => item.id === facility) || !this.warehouses.some((item) => item.id === warehouse)) { this.initError = true; return; }
      this.facility = facility; this.warehouse = warehouse; this.from = query.from; this.to = query.to; this.operation = query.operation;
      await this.refresh();
      const row = this.employeeRows.find((item) => String(item.employeeId ?? 'unattributed') === query.employee && item.operation === query.operation);
      if (!row) { this.error = true; return; }
      await this.openEmployee(null, row);
      if (['summary', 'hourly', 'daily', 'records'].includes(query.tab)) this.profileTab = query.tab;
      if (query.day) this.selectDetailDay(query.day);
    },
    async copyEmployeeLink() {
      const query = { facility: this.facility, warehouse: this.warehouse, employee: this.employee, operation: this.employeeOperation, from: this.from, to: this.to, tab: this.profileTab, day: this.detailDate || undefined };
      const href = this.$router.resolve({ path: this.$route.path, query }).href;
      try { await navigator.clipboard.writeText(new URL(href, window.location.origin).href); this.linkStatus = 'copied'; }
      catch { this.linkStatus = 'linkError'; }
    },

    t(key) { return this.$t(`employeePerformance.${key}`); },
    value(value) { return value === null || value === undefined ? this.t('unavailable') : Number(value).toLocaleString(); },
    percent(value) { return value === null || value === undefined ? '—' : `${value}%`; },
    rowKey(row) { return `${row.employeeId ?? 'unattributed'}:${row.operation}`; },
    async initialize() {
      this.optionsVersion += 1; this.facilitySearchVersion += 1; this.warehouseSearchVersion += 1;
      this.facility = null; this.warehouse = null; this.warehouses = []; this.facilities = [];
      this.initError = false; this.optionsLoading = false; this.facilityLoading = false;
      this.managerAccess = false;
      this.selectWarehouse();
      const version = this.optionsVersion;
      try {
        await employeePerformanceService.options({ accessOnly: 1 });
        if (version === this.optionsVersion) { this.managerAccess = true; await this.restoreEmployeeLink(); }
      } catch {
        if (version === this.optionsVersion) this.initError = true;
      }
    },
    async searchFacilities(search, update) {
      this.facilitySearchVersion += 1;
      const version = this.facilitySearchVersion;
      this.facilityLoading = true;
      try {
        const data = await employeePerformanceService.options({ search });
        if (version !== this.facilitySearchVersion) return;
        update(() => {
          const selected = this.facilities.find((item) => item.id === this.facility);
          this.facilities = data.facilities;
          if (selected && !this.facilities.some((item) => item.id === selected.id)) this.facilities.unshift(selected);
          this.initError = false;
        });
      } catch {
        if (version === this.facilitySearchVersion) { this.initError = true; update(() => {}); }
      } finally { if (version === this.facilitySearchVersion) this.facilityLoading = false; }
    },
    async searchWarehouses(search, update) {
      const { facility } = this;
      this.warehouseSearchVersion += 1;
      const version = this.warehouseSearchVersion;
      if (!facility) { update(() => {}); return; }
      this.optionsLoading = true;
      try {
        const data = await employeePerformanceService.options({ facility, search });
        if (version !== this.warehouseSearchVersion || facility !== this.facility) return;
        update(() => {
          const selected = this.selectedWarehouse;
          this.warehouses = data.warehouses;
          if (selected && !this.warehouses.some((item) => item.id === selected.id)) this.warehouses.unshift(selected);
          this.initError = false;
        });
      } catch {
        if (version === this.warehouseSearchVersion && facility === this.facility) { this.initError = true; update(() => {}); }
      } finally { if (version === this.warehouseSearchVersion) this.optionsLoading = false; }
    },
    async selectFacility() {
      this.optionsVersion += 1;
      const version = this.optionsVersion;
      this.warehouseSearchVersion += 1;
      this.warehouse = null; this.warehouses = []; this.initError = false;
      await this.selectWarehouse();
      const { facility } = this;
      if (!facility) { this.optionsLoading = false; return; }
      this.optionsLoading = true;
      try {
        const options = await employeePerformanceService.options({ facility });
        if (version !== this.optionsVersion || facility !== this.facility) return;
        this.warehouses = options.warehouses;
        if (this.warehouses.some((item) => item.id === facility)) {
          this.warehouse = facility;
          await this.selectWarehouse();
        }
      } catch { if (version === this.optionsVersion) this.initError = true; } finally { if (version === this.optionsVersion) this.optionsLoading = false; }
    },
    async selectWarehouse() {
      this.requestVersion += 1; this.detailVersion += 1; this.report = null; this.loading = false; this.showEmployee = false; this.showTargets = false;
      this.targetDate = this.tomorrow; this.error = false;
      this.from = this.selectedWarehouse?.today || ''; this.to = this.from;
      if (this.from) await this.refresh();
      else if (this.warehouse) await this.refresh();
    },
    async resetEmployee() { this.showEmployee = false; this.detailVersion += 1; await this.refresh(); },
    async setPeriod(month) {
      const today = this.selectedWarehouse?.today;
      if (!today) return;
      this.to = today; this.from = month ? `${today.slice(0, 7)}-01` : today;
      await this.refresh();
    },
    criteria() {
      return {
        warehouse: this.warehouse, facility: this.facility || undefined, from: this.from, to: this.to, operation: this.operation,
      };
    },
    async refresh() {
      if (!this.facility || !this.warehouse) return;
      this.detailVersion += 1; this.showEmployee = false;
      await loadLatestPerformance(this, () => employeePerformanceService.report({ ...this.criteria(), operation: '' }));
    },
    trendWidth(day) {
      const max = Math.max(1, ...this.report.daily.filter((d) => d.operation === day.operation).map((d) => d.output || 0));
      return day.output === null ? 0 : (100 * day.output) / max;
    },
    async openEmployee(event, row) {
      this.profileTab = 'summary';
      this.employee = row.employeeId ?? 'unattributed'; this.employeeName = row.employeeName;
      this.employeeOperation = row.operation; this.detailPage = 1; this.detailDate = ''; this.showEmployee = true;
      await this.loadDetail();
    },
    profileWidth(row, rows) {
      return row.output === null ? 0 : (100 * row.output) / Math.max(1, ...rows.map(item => item.output || 0));
    },
    selectDetailDay(date) {
      const profile = this.detail?.employeeProfile;
      const day = profile?.daily.find(item => item.date === date);
      if (!day) return;
      this.detailDate = date; profile.date = date; profile.hourly = day.hourly;
    },
    async loadDetail() {
      this.detailVersion += 1; const version = this.detailVersion; this.detailLoading = true; this.detailError = false; this.detail = null;
      const criteria = {
        ...this.criteria(), employee: this.employee, operation: this.employeeOperation, page: this.detailPage,
      };
      if (this.detailDate) criteria.hourlyDate = this.detailDate;
      try {
        const detail = await employeePerformanceService.report(criteria);
        if (version === this.detailVersion) this.detail = detail;
      } catch { if (version === this.detailVersion) this.detailError = true; } finally { if (version === this.detailVersion) this.detailLoading = false; }
    },
    async saveTarget() {
      if (this.saving) return;
      this.saving = true; this.targetError = false;
      const { warehouse } = this;
      try {
        const targets = await employeePerformanceService.saveTarget({
          warehouse, operation: this.targetOperation, dailyTarget: this.targetValue, effectiveFrom: this.targetDate,
        });
        if (warehouse === this.warehouse && this.report) this.report = { ...this.report, targets };
      } catch { this.targetError = true; } finally { this.saving = false; }
    },
  },
};
</script>

<style scoped>
.employee-performance { max-width: 1600px; width: 100%; min-width: 0; margin: auto; }
.ep-filters { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 16px; align-items: end; }
.ep-filters > * { width: 100%; min-width: 0; }
.employee-performance :deep(.q-table th) { white-space: normal; max-width: 210px; }
.employee-performance :deep(.q-table td) { white-space: normal; }
.metric-card { border: 1px solid #ddd; border-radius: 0; }
.trend-row { display: grid; grid-template-columns: 190px 1fr 140px; gap: 16px; align-items: center; padding: 9px 0; }
.trend-track { height: 9px; border-radius: 0; background: #edf0f3; overflow: hidden; }
.trend-track > div { height: 100%; background: var(--q-primary); border-radius: 0; }
.detail-card { width: 900px; max-width: 95vw; border-radius: 0; }
@media(max-width: 600px) { .trend-row { grid-template-columns: 1fr 1fr; gap: 8px; } .trend-row > .trend-track { grid-column: 1 / -1; } }
</style>

<style scoped>
.detail-card :deep(.q-markup-table) { max-width: 100%; overflow-x: auto; }
.detail-card :deep(td), .detail-card :deep(th) { white-space: normal; overflow-wrap: anywhere; }
.profile-summary { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }
.profile-summary > div { background: #f4f6f8; padding: 12px; border-radius: 0; }
.profile-summary small, .profile-summary strong { display: block; }
.profile-summary strong { font-size: 24px; }
.hour-row { display: grid; grid-template-columns: 135px minmax(40px, 1fr) 145px; gap: 10px; align-items: center; font-size: 12px; min-height: 28px; }
@media(max-width: 600px) { .profile-summary { grid-template-columns: 1fr; } .hour-row { grid-template-columns: 110px minmax(30px, 1fr) 110px; gap: 6px; } }
</style>

<style scoped>
.employee-profile { background: white; border-radius: 0; }
.employee-profile > .q-card__section { width: 100%; max-width: 1440px; margin: 0 auto; padding: 24px 32px; }
.employee-profile > .q-card__section:first-child :deep(.q-space) { display: none; }
.employee-profile > .q-card__section:first-child .text-h6 { flex: 1; min-width: 0; overflow-wrap: anywhere; }
.employee-profile > .q-card__section:first-child { flex-wrap: nowrap; align-items: flex-start; background: white; max-width: none; border-bottom: 1px solid #e5e9ef; }
.profile-tabs { background: white; border-bottom: 1px solid #e5e9ef; padding: 0 20px; }
.employee-profile .profile-summary { grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 20px; }
.employee-profile .profile-summary > div { background: white; border: 1px solid #ddd; padding: 16px; border-radius: 0; }
.employee-profile .profile-summary strong { font-size: 28px; margin-top: 8px; }
.employee-profile section { background: white; border: 1px solid #ddd; border-radius: 0; padding: 16px; }
.employee-profile .hour-row { min-height: 36px; font-size: 14px; }
@media(max-width: 800px) { .employee-profile .profile-summary { grid-template-columns: repeat(2, minmax(0, 1fr)); } .employee-profile > .q-card__section { padding: 16px; } .employee-profile section { padding: 12px; } }
@media(max-width: 450px) { .employee-profile .profile-summary { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; } .employee-profile .profile-summary > div { padding: 12px; } .employee-profile .hour-row { grid-template-columns: 90px minmax(30px, 1fr) 100px; font-size: 11px; } }
</style>

<style scoped>
.employee-performance :deep(.q-card),
.employee-performance :deep(.q-table__container),
.employee-performance :deep(.q-field__control),
.employee-performance :deep(.q-btn),
.employee-performance :deep(.q-banner),
.employee-profile :deep(.q-card),
.employee-profile :deep(.q-table__container),
.employee-profile :deep(.q-field__control),
.employee-profile :deep(.q-btn),
.employee-profile :deep(.q-banner),
.detail-card :deep(.q-card),
.detail-card :deep(.q-table__container),
.detail-card :deep(.q-field__control),
.detail-card :deep(.q-btn),
.detail-card :deep(.q-banner) { border-radius: 0; }
</style>

<style scoped>
@media (max-width: 600px) {
  .ep-filters > :nth-child(-n+2), .ep-filters > :last-child { grid-column: 1 / -1; }
}
</style>
